import { gql, useQuery } from '@apollo/client';
import { type CalendarDate } from '@internationalized/date';
import * as React from 'react';

import { PaginationDirection, type InvestmentActivity } from '../../../__generated__/globalTypes';
import { formatCalendarDate } from '../../../utils/formatting';
import { getPresetDateRange, useDateRangeDisplayText, type CalendarDateRange } from '../../DateRangePicker';

import BaseInvestmentTransactionsTable from './BaseInvestmentTransactionsTable';

import type {
  GetInvestmentTransactionCoverageQuery,
  GetInvestmentTransactionCoverageQueryVariables,
  GetInvestmentTransactionsForTableQuery,
  GetInvestmentTransactionsForTableQueryVariables,
} from './__generated__/InvestmentTransactionsTable.gql';

const PAGE_SIZE = 30;

const GET_INVESTMENT_TRANSACTIONS_FOR_TABLE = gql`
  query GetInvestmentTransactionsForTable(
    $financialAccountId: ID!
    $pagination: PaginationInput!
    $filters: InvestmentTransactionFilters
  ) {
    getInvestmentTransactions(financialAccountId: $financialAccountId, pagination: $pagination, filters: $filters) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          ...InvestmentTransactionTableRow
        }
      }
    }
  }
  ${BaseInvestmentTransactionsTable.Fragment}
`;

const GET_INVESTMENT_TRANSACTION_COVERAGE = gql`
  query GetInvestmentTransactionCoverage($financialAccountId: ID!, $filters: InvestmentTransactionFilters) {
    hasInvestmentTransactions(financialAccountId: $financialAccountId, filters: $filters) {
      exists
      count
      startDate
      endDate
    }
  }
`;

interface IInvestmentTransactionsTableProps {
  financialAccountId: string;
  dateRange: CalendarDateRange;
  onDateRangeChange: (range: CalendarDateRange) => void;
  /** The activity group to keep; null shows all activity. */
  activity: InvestmentActivity | null;
  onActivityChange: (activity: InvestmentActivity | null) => void;
}

/**
 * One investment account's transactions inside the selected range and activity group, 30 a page,
 * the next page loading on scroll. A second read, of the account's whole stored history, picks the
 * empty state and adds a note when that history starts after the range does. With a group chosen,
 * a third read counts that group's rows across the history for the empty state.
 */
const InvestmentTransactionsTable = ({
  financialAccountId,
  dateRange,
  onDateRangeChange,
  activity,
  onActivityChange,
}: IInvestmentTransactionsTableProps) => {
  const { data, loading, error, fetchMore } = useQuery<
    GetInvestmentTransactionsForTableQuery,
    GetInvestmentTransactionsForTableQueryVariables
  >(GET_INVESTMENT_TRANSACTIONS_FOR_TABLE, {
    variables: {
      financialAccountId,
      pagination: { cursor: undefined, limit: PAGE_SIZE, direction: PaginationDirection.Forward },
      // Sent as the ISO day: a CalendarDate would serialize as an object the scalar rejects.
      filters: {
        startDate: dateRange.start.toString() as unknown as CalendarDate,
        endDate: dateRange.end.toString() as unknown as CalendarDate,
        activity: activity ?? undefined,
      },
    },
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: coverageData,
    loading: coverageLoading,
    error: coverageError,
  } = useQuery<GetInvestmentTransactionCoverageQuery, GetInvestmentTransactionCoverageQueryVariables>(
    GET_INVESTMENT_TRANSACTION_COVERAGE,
    { variables: { financialAccountId } }
  );

  const {
    data: matchingData,
    loading: matchingLoading,
    error: matchingError,
  } = useQuery<GetInvestmentTransactionCoverageQuery, GetInvestmentTransactionCoverageQueryVariables>(
    GET_INVESTMENT_TRANSACTION_COVERAGE,
    { variables: { financialAccountId, filters: { activity } }, skip: activity === null }
  );

  const subtitle = useDateRangeDisplayText(dateRange);
  const rows = React.useMemo(() => data?.getInvestmentTransactions.edges.map((edge) => edge.node) ?? [], [data]);
  const pageInfo = data?.getInvestmentTransactions.pageInfo;
  const coverage = coverageData?.hasInvestmentTransactions;
  // What the empty state counts: the chosen group's rows, or every row with none chosen.
  const matching = activity === null ? coverage : matchingData?.hasInvestmentTransactions;

  const endCursor = pageInfo?.endCursor;
  // Awaited: the scroll hook holds its guard only until the returned promise settles.
  const handleLoadMore = React.useCallback(async () => {
    if (endCursor) {
      await fetchMore({
        variables: { pagination: { cursor: endCursor, limit: PAGE_SIZE, direction: PaginationDirection.Forward } },
      });
    }
  }, [endCursor, fetchMore]);

  const historyStart: CalendarDate | null = coverage?.startDate ?? null;
  const footer =
    historyStart && historyStart.compare(dateRange.start) > 0
      ? `Activity available from ${formatCalendarDate(historyStart, 'medium')}.`
      : undefined;

  return (
    <BaseInvestmentTransactionsTable
      rows={rows}
      subtitle={subtitle}
      // Until every read answers, an empty table cannot tell which empty state it is in.
      isLoading={rows.length === 0 && (loading || coverageLoading || matchingLoading)}
      isLoadingMore={loading && rows.length > 0}
      // A failed history read costs only the note while there are rows to show.
      hasError={Boolean(error) || (rows.length === 0 && Boolean(coverageError ?? matchingError))}
      hasNextPage={pageInfo?.hasNextPage ?? false}
      onLoadMore={handleLoadMore}
      footer={footer}
      emptyStateProps={
        coverage?.count === 0
          ? undefined
          : {
              hasTransactionsResult: matching,
              currentDateRange: dateRange,
              // The account's own history: All data starts at the household's oldest bank transaction.
              onViewTransactions: () => {
                if (historyStart) {
                  onDateRangeChange(getPresetDateRange('allTime', historyStart));
                }
              },
            }
      }
      selectedActivity={activity}
      onActivityChange={onActivityChange}
    />
  );
};

export default InvestmentTransactionsTable;
